import React, { useEffect, useState } from 'react';
import glamorous from 'glamorous';
import { parse } from 'url';
import { browserHistory } from '../components/AppContent';
// import { showNotification } from '../App';
import { RvLoader } from '../components/Loader';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

declare const GraphQLApiUrl: string;

type ValidationStatus = 'loading' | 'error';

interface ExternalAccessResponse {
    accessToken: string;
    tokenType: string;
    expiresIn: number;
    redirectUrl: string;
}

const ExternalAccessValidationWrapper = glamorous.div<{ theme?: any }>(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    textAlign: 'center',
    [`& .${'message'}`]: {
        fontSize: '18px',
        marginTop: '24px',
        fontWeight: 600,
        color: '#444',
    },
}));

const ErrorWrapper = glamorous.div<{ theme?: any }>(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    textAlign: 'center',
    padding: '0 2rem',
    [`& .${'error-icon'}`]: {
        fontSize: '64px',
        color: '#c0392b',
        marginBottom: '16px',
    },
    [`& .${'error-title'}`]: {
        fontSize: '22px',
        fontWeight: 700,
        color: '#333',
        margin: '0 0 12px 0',
    },
    [`& .${'error-detail'}`]: {
        fontSize: '15px',
        color: '#666',
        maxWidth: '420px',
        lineHeight: 1.6,
        margin: '0 0 16px 0',
    },
    [`& .${'error-support'}`]: {
        fontSize: '13px',
        color: '#999',
    },
}));

const getQueryParams = () => {
    // eslint-disable-next-line no-restricted-globals
    const parsed = parse(location.href, true);

    return {
        token: (parsed.query ? (parsed.query.token as string) : '') || '',
        tenant: (parsed.query ? (parsed.query.tenant as string) : '') || '',
    };
};

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const buildExternalAccessUrl = (token: string, tenant: string) => {
    const rawBase = trimTrailingSlash(GraphQLApiUrl);

    // For localhost, it should be https://local.redview.com.au
    const baseUrl = /^https?:\/\/localhost(:\d+)?$/.test(rawBase)
        ? 'https://local.redview.com.au'
        : rawBase;
    return `${baseUrl}/external/api/externalaccess/enquirydetail?token=${encodeURIComponent(token)}&tenant=${encodeURIComponent(tenant)}`;
};

const storeExternalAccessToken = (result: ExternalAccessResponse, tenant: string) => {
    try {
        sessionStorage.setItem('external.accessToken', result.accessToken);
        sessionStorage.setItem('external.tokenType', result.tokenType || 'Bearer');
        sessionStorage.setItem('external.expiresIn', String(result.expiresIn || ''));
        // Standard keys used by the Apollo authLink and AppContent
        sessionStorage.setItem('accessToken', result.accessToken);
        sessionStorage.setItem('tenantId', tenant);
    } catch {
        // ignore storage issues
    }
};

const redirectToNextPage = (redirectUrl: string) => {
    // Leave this simple for now so it can be swapped later.
    browserHistory.push(redirectUrl || '/');
};

const ExternalAccessValidation: React.FC = () => {
    const [status, setStatus] = useState<ValidationStatus>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        let isMounted = true;

        const validate = async () => {
            const { token, tenant } = getQueryParams();

            if (!token) {
                if (!isMounted) return;
                setStatus('error');
                setErrorMessage('Missing access token.');
                // showNotification(null, 'Missing access token.', 'error');
                return;
            }

            if (!tenant) {
                if (!isMounted) return;
                setStatus('error');
                setErrorMessage('Missing tenant.');
                // showNotification(null, 'Missing tenant.', 'error');
                return;
            }

            try {
                const response = await fetch(buildExternalAccessUrl(token, tenant), {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                    },
                });

                let result: ExternalAccessResponse | any = null;
                try {
                    result = await response.json();
                } catch {
                    result = null;
                }

                if (!response.ok) {
                    const message =
                        (result && (result.error_description || result.error)) ||
                        'Unable to validate external access.';

                    if (!isMounted) return;
                    setStatus('error');
                    setErrorMessage(message);
                    // showNotification(null, message, 'error');
                    return;
                }

                if (!result || !result.accessToken || !result.redirectUrl) {
                    if (!isMounted) return;
                    setStatus('error');
                    setErrorMessage('External access validation returned an invalid response.');
                    // showNotification(null, 'External access validation returned an invalid response.', 'error');
                    return;
                }

                storeExternalAccessToken(result, tenant);

                if (!isMounted) return;
                redirectToNextPage(result.redirectUrl);
            } catch {
                if (!isMounted) return;
                setStatus('error');
                setErrorMessage('An unexpected error occurred while validating access.');
                // showNotification(null, 'An unexpected error occurred while validating access.', 'error');
            }
        };

        validate();

        return () => {
            isMounted = false;
        };
    }, []);

    if (status === 'error') {
        return (
            <ErrorWrapper>
                <ErrorOutlineIcon className="error-icon" />
                <p className="error-title">Access no longer available</p>
                {/* <p className="error-detail">{errorMessage || 'We were unable to validate your access.'}</p>
                <p className="error-support">Please contact support if the issue continues.</p> */}
            </ErrorWrapper>
        );
    }

    return (
        <ExternalAccessValidationWrapper>
            <RvLoader size="large" />
            <div className="message">Validating access…</div>
            <div className="sub-message">Please wait while we prepare your secure form access.</div>
        </ExternalAccessValidationWrapper>
    );
};

export default ExternalAccessValidation;